// material-ui
import { Grid, Snackbar } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import React, { ReactElement } from 'react';
import update from 'react-addons-update';

// apis
import {
  getAllPrebidPublishers,
  IPublisher,
  ResourceActiveDisabled,
  ResourceActiveEnabled,
  ResourceActiveUnknown,
  uploadNetworkSlatefile,
  uploadSSAIHouseAd,
  uploadSSAIPadderAd,
} from '~/src/dash/api/console-api';
import {
  generalGetLiveLogs,
  getStreamResponseLogsQuery,
  getStreamStartLogsQuery,
} from '~/src/dash/api/live_logs/controller';
import { filterLiveLogs } from '~/src/dash/api/live_logs/helpers';
import { IStreamResponseData, IStreamStartData } from '~/src/dash/api/live_logs/models';
import {
  createNetwork,
  deleteNetwork,
  deleteNetworkChannel,
  deleteNetworkInVideoUnit,
  deleteNetworkPublisherMap,
  deleteNetworkTracker,
  ENetworkRole,
  getSSAIHouseAdsByPublisherID,
  INetwork,
  INetworkChannel,
  INetworkInVideoUnit,
  INetworkTracker,
  IParam,
  ISSAIHouseAd,
  ISSAIPadderAd,
  NETWORK_AD_URL_SITE_PREFIX,
  NetworkChannelField,
  NetworkDemandSourceType,
  NetworkInVideoUnitPartner,
  NetworkInVideoUnitPartnerTriplelift,
  NetworkStreamType,
  NetworkTrackerEventTypeStart,
  streamTypeStringToNetworkStream,
  transformNetwork,
  updateNetwork,
  uploadSSAIHouseAdByURL,
} from '~/src/dash/api/network';
import { IHttpResponse } from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';
import { CopyButton } from '~/src/dash/components/button';
import { LiveLogsWrapper } from '~/src/dash/components/live_logs/live_logs_wrapper';
import { LiveLogsGeneralTable } from '~/src/dash/components/live_logs/live-logs-general-table';
// components
import { LiveData } from '~/src/dash/components/live-data/live-data';
import { LogStreamResponseTable } from '~/src/dash/components/logs-stream-response-table';
import { ModelSnackBarProgressBarContainer } from '~/src/dash/components/model-snackbar-progresbar-container';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import BasicNetworkSettingsForm, {
  RenderBasicNetworkSettings,
} from '~/src/dash/components/networks/basic-network-settings-form';
import HouseAdsUploadForm from '~/src/dash/components/networks/house-ads-form';
import { NetworkBetaFeatures } from '~/src/dash/components/networks/network-beta-features';
import InventorySplitForm, {
  RenderSplitRoutes,
} from '~/src/dash/components/networks/network-inventory-split-form';
import StreamURLForm from '~/src/dash/components/networks/network-stream-url-form';
import OptionalNetworkSettingsForm, {
  RenderOptionalNetworkSettings,
} from '~/src/dash/components/networks/optional-network-settings-form';
import {
  INetworkPublisherRowData,
  IPublisherMap,
  PublisherRolesTable,
} from '~/src/dash/components/publisher-roles-table';
import { SettingsAppBar } from '~/src/dash/components/settings-app-bar';
import { ITab, SettingsTabs } from '~/src/dash/components/settings-tabs';
import {
  ICustomRenderCellJSXProps,
  IListViewTableRow,
  ListViewTableDataCellRenderType,
} from '~/src/dash/components/tables/ListViewTable/common';
import { Text } from '~/src/dash/components/text';
import { checkURL } from '~/src/dash/components/vast-editor';
import Wizard from '~/src/dash/components/wizard';
import { Col, Cols, Div, Panel, Panels, Row, Rows, spacing } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { getContactEmail, getStreamDomain } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  extractRouteParams,
  navigate,
  RouteNetworkList,
  RouteNetworkSettingsExisting,
} from '~/src/dash/router/routes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatTime } from '~/src/dash/utils/date';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
// other
import { makeid } from '~/src/dash/utils/makeid';
import { IUploadSingleMediafileInput, uploadSingleMediafile } from '~/src/dash/utils/network-utils';
import { IPermissionCheck, IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked, hasRole, ROLE_SUPER } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { ChipTranscodeStatus } from '~/src/dash/utils/transcode-status';
import UploadManager from '~/src/dash/utils/upload_manager';
// views
import { LoadingView } from '~/src/dash/views/loading-view';
import {
  DEFAULT_AUDIO_NORMALIZATION_LEVEL,
  EPadderType,
  EXTENSION_TYPE_MP4,
} from '~/src/dash/views/networks/network-settings-view-constants';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

// NOTE: For settings views, it is preferred to follow this model where there is a child view
// that is responsible for the majority of the react code and several various parent views
// connected to various routes, in this case, network-settings-clone-view, network-settings-existing-view,
// and network-settings-new-view, which pass on the currentNetwork as props to this view.
// The parent view can pass on a blank resource, a clean clone or the resource from the api.

// The resource is then placed directly into state. You can note that within state, fields that
// belong to the resource (and thus are updated via API) should be capitalized, and the other items
// in state (like loading, snackbarError, pageNumber, etc) should be lower case.  The state can then be
// destructured into the resouce when creating or updating since we send back the whole object
// to the API, making it easier to update APIs when adding new fields.

// NOTE: Please add comments to IProps and IState to explain what certain variables do, especially if it
// may be unclear.  Try to keep them grouped if they may be related. Lastly, try to keep comments to IProps
// and IState rather than in the state declaration to keep it clean.

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  currentNetwork: INetwork;
  cloneReferenceID: number;
  isClone: boolean;
}

// NOTE: IState extends INetwork since we will place the network directly into state

interface IState extends IBaseState, INetwork {
  // error msging to user
  snackbarMessage: string;
  snackbarError: boolean;
  // indicates initial componentDidMount loads are completed
  loaded: boolean;
  // indicates an api call to update is in progress
  isUpdating: boolean;
  // params for URL Editor
  streamLiveParams: IParam[];
  // URL displayed
  stitchedURL: string;
  // All the current endpoint params, in url format
  EndpointURLParams: string;
  // publishers for roles tab
  publishers: IPublisher[];
  // once used has tried to save once, the error displays will appear
  saveAttempted: boolean;

  // used to know if we display the live logs popup or not for network
  streamStartLiveLogs: IStreamStartData[];
  streamResponseLiveLogs: IStreamResponseData[];

  uploadingSlateMedia: boolean;
  uploadingSSAIHouseAd: boolean;
  uploadingSSAIPadderAd: boolean;
  HouseAds: ISSAIHouseAd[];
  publisherSSAIHouseAds: ISSAIHouseAd[];
  ssaiHouseAdsDropdownRows: IMenuList;
  ssaiHouseAdsTableRows: IListViewTableRow[];
  ssaiHouseAdsLoadingState: LoadingState;
  refreshingLiveLogs: boolean;
  refreshingLiveLogsTimestamp: string;
}

const WIZARD_STEP_NAMES = ['Basic Settings', 'Inventory Route', 'Stream URL', 'Optional Settings'];

const RENDER_ON_DEMAND_STEP_NAMES = [
  'Basic Settings',
  'House Ads',
  'Inventory Route',
  'Stream URL',
  'Optional Settings',
];

type TranscodeStatusCodes = 'unknown' | 'stale' | 'complete' | 'processing';

export const NetworkSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // NOTE: Use styles when the CSS is used more than once
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 6,
        paddingLeft: theme.spacing.unit * 6 + 'px !important',
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
        [theme.breakpoints.up('md')]: {},
      },
      mainGridItem: {
        marginLeft: 40,
        marginTop: 25,
      },
      addTypography: {
        fontSize: 16,
        fontWeight: 500,
        color: '#5865E8',
        marginLeft: 5,
        paddingBottom: 15,
        cursor: 'pointer',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      ...this.props.currentNetwork,
      snackbarMessage: '',
      snackbarError: false,
      loaded: false,
      isUpdating: false,
      saveAttempted: false,
      stitchedURL: '',
      streamLiveParams: [] as IParam[],
      publishers: [] as IPublisher[],
      streamStartLiveLogs: [] as IStreamStartData[],
      streamResponseLiveLogs: [] as IStreamResponseData[],
      uploadingSlateMedia: false,
      AudioNormalizeTarget:
        this.props.currentNetwork.AudioNormalizeTarget === undefined
          ? -DEFAULT_AUDIO_NORMALIZATION_LEVEL
          : this.props.currentNetwork.AudioNormalizeTarget,
      EnableAudioNormalization:
        this.props.currentNetwork.EnableAudioNormalization === undefined
          ? EBool.EBool_FALSE
          : this.props.currentNetwork.EnableAudioNormalization,
      EnableTranscoderV3:
        this.props.currentNetwork.EnableTranscoderV3 === undefined
          ? EBool.EBool_TRUE
          : this.props.currentNetwork.EnableTranscoderV3,
      EnableStartFromTop:
        this.props.currentNetwork.EnableStartFromTop === undefined
          ? EBool.EBool_FALSE
          : this.props.currentNetwork.EnableStartFromTop,
      EnableMatchVariantLadder:
        this.props.currentNetwork.EnableMatchVariantLadder === undefined
          ? EBool.EBool_FALSE
          : this.props.currentNetwork.EnableMatchVariantLadder,
      EnableJingle:
        this.props.currentNetwork.EnableJingle === undefined
          ? EBool.EBool_FALSE
          : this.props.currentNetwork.EnableJingle,
      EnableBumper:
        this.props.currentNetwork.EnableBumper === undefined
          ? EBool.EBool_FALSE
          : this.props.currentNetwork.EnableBumper,
      displayTranscoderV3Modal: false,
      // all available house ad choices from the publisher for the network
      publisherSSAIHouseAds: [] as ISSAIHouseAd[],
      ssaiHouseAdsDropdownRows: {} as IMenuList,
      ssaiHouseAdsTableRows: [] as IListViewTableRow[],
      uploadingSSAIHouseAd: false,
      ssaiHouseAdsLoadingState: LoadingStateLoading,
      refreshSSAIHouseAdsInterval: undefined,
      refreshingLiveLogs: false,
      refreshingLiveLogsTimestamp: '',
      uploadingSSAIPadderAd: false,
      PadderAds: this.props.currentNetwork.PadderAds as ISSAIPadderAd[],
    };

    uploadManager = new UploadManager();

    isNewOrClonedNetwork = (): boolean => this.state.ID === 0;

    /**
     * Called when param values in the endpoint builder changes
     */
    onEndpointBuilderChange = (endpointURLParams: string) => {
      const newEndpoint = getStreamDomain() + endpointURLParams;

      // replace stitchedURL value by user-defined ones
      const stitchedURL = this.updateCustomParams(newEndpoint);
      this.setState(
        {
          stitchedURL,
        },
        () => {
          // generate ad urls for inventory splits due to the change
          for (let i = 0; i < this.state.Channels.length; i++) {
            if (this.state.Channels[i].DemandSourceType === NetworkDemandSourceType.TYPE_PUBLICA) {
              this.handleChangeNetworkChannel(
                i,
                'AdURL',
                this.generatePublicaAdURL((this.state.Channels[i].SiteID || '').toString()),
              );
            }
          }
        },
      );
    };

    // NOTE: We should try to build helper functions for componentDidMount, to keep the process clean
    // and readable, especially when we add more steps
    componentDidMount() {
      this.generateStitchedURL();
      this.loadPublishersForRolesTab();
      this.getLiveLogs();
      this.loadPublisherSSAIHouseAds(this.props.session.publisher.ID);
    }

    componentWillUnmount() {
      clearInterval(this.state.refreshSSAIHouseAdsInterval);
    }

    // NOTE: In general, like we do with props and state, we should try to organize the functions
    // within a view/component to keep the code clean.  In addition, we should try to name the functions
    // as well as possible to show what is happening and if more explanation is needed, use a comment
    // above the function definition

    // initialize endpoint and params
    generateStitchedURL = (): void => {
      let endpoint: string = '';
      // set default params if creating a new network, else use existing endpointURLParams
      if (this.state.EndpointURLParams === '') {
        endpoint = getStreamDomain() + this.defaultEndpointParams();
      } else {
        // adding cache_buster to default params
        if (this.state.EndpointURLParams.includes('cb=[CACHEBUSTER]')) {
          endpoint = getStreamDomain() + this.state.EndpointURLParams;
        } else {
          endpoint = getStreamDomain() + 'cb=[CACHEBUSTER]&' + this.state.EndpointURLParams;
        }
      }
      const stitchedURL = this.updateCustomParams(endpoint);
      this.setState({
        stitchedURL,
      });
    };

    // for super users, get all publishers for the roles tab
    loadPublishersForRolesTab = (): void => {
      if (this.isSuper()) {
        getAllPrebidPublishers()
          .then((publishers: IPublisher[]) => {
            this.setState({
              publishers,
              loaded: true,
              ID: this.props.isClone ? 0 : this.state.ID,
            });
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'Error loading publishers',
              snackbarError: true,
              publishers: [] as IPublisher[],
              loaded: true,
              ID: this.props.isClone ? 0 : this.state.ID,
            });
          });
        return;
      }
      this.setState({
        loaded: true,
      });
    };

    // default params for a stream URL based on stream type
    defaultEndpointParams = (): string => {
      const endpointParams: { [x: string]: string } = {};
      endpointParams.network_id = this.props.currentNetwork.ID.toString() || '[NETWORK_ID]';
      endpointParams.live = this.state.StreamType === NetworkStreamType.TYPE_LIVE ? '1' : '0';
      endpointParams.avod = '1';
      endpointParams.cb = '[CACHEBUSTER]';
      if (this.state.StreamType === NetworkStreamType.TYPE_VOD) {
        endpointParams.u = '[INSERT_VOD_ASSET_URL]';
      }
      return Object.entries(endpointParams)
        .map(([key, value]) => {
          return `${key}=${value}`;
        })
        .join('&');
    };

    refreshNetworkEndpoint = () => {
      const defaultParams = this.defaultEndpointParams();

      // remove default params in case stream type has changed
      const streamLiveParams = this.state.streamLiveParams.filter(
        (param) =>
          param.name !== 'network_id' &&
          param.name !== 'live' &&
          param.name !== 'avod' &&
          param.name !== 'u',
      );
      const userDefinedParams = streamLiveParams
        .map((param) => {
          return `${param.name}=${param.value}`;
        })
        .join('&');

      const endpointURLParams = defaultParams + '&' + userDefinedParams;
      const endpoint = getStreamDomain() + endpointURLParams;
      const stitchedURL = this.updateCustomParams(endpoint);
      this.setState({
        stitchedURL,
        endpointURLParams,
      });
    };

    // Get all the live logs data for this network
    getLiveLogs = () => {
      if (this.isNewOrClonedNetwork()) {
        return;
      }
      this.setState(
        {
          refreshingLiveLogs: true,
        },
        () => {
          // Stream Start
          const networkID = this.state.ID;
          const streamStartLogsQuery = getStreamStartLogsQuery(networkID);

          generalGetLiveLogs<IStreamStartData[]>(
            streamStartLogsQuery,
            this.props.session.publisher.ID,
            this.props.user.user.Username,
          ).then(([data, err]) => {
            if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
              this.setState({
                streamStartLiveLogs: data,
                refreshingLiveLogs: false,
                refreshingLiveLogsTimestamp: formatTime(
                  moment(),
                  this.props.user.publisher.Timezone,
                ),
              });
            }
            if (err !== undefined) {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error Live Logs for Stream Start',
                snackbarError: true,
                refreshingLiveLogs: false,
              });
            }
          });

          // Stream Response
          const streamResponseLogsQuery = getStreamResponseLogsQuery(networkID);

          generalGetLiveLogs<IStreamResponseData[]>(
            streamResponseLogsQuery,
            this.props.session.publisher.ID,
            this.props.user.user.Username,
          ).then(([data, err]) => {
            if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
              this.setState({
                streamResponseLiveLogs: data,
                refreshingLiveLogs: false,
                refreshingLiveLogsTimestamp: formatTime(
                  moment(),
                  this.props.user.publisher.Timezone,
                ),
              });
            }
            if (err !== undefined) {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error Live Logs for Stream Response',
                snackbarError: true,
                refreshingLiveLogs: false,
              });
            }
          });
        },
      );
    };

    // Update param values with user defined ones via the Customize Params Value Modal
    // Adds new params, removes old params not in new params
    updateCustomParams = (endpoint: string): string => {
      // params to ignore - use this if param value is dynamic in endpoint builder
      const ignoredParams: string[] = ['live', 'network_id'];
      let currentParams = [...this.state.streamLiveParams];
      const newParams = this.getParamsFromURL(endpoint);
      newParams.forEach((newParam: IParam) => {
        // need to update the value on min / max ad duration even if it already exists in currentParams,
        // so must remove it from currentParams or the value will not be updated
        if (newParam.name === 'min_ad_duration') {
          currentParams = currentParams.filter((param) => param.name !== 'min_ad_duration');
        } else if (newParam.name === 'max_ad_duration') {
          currentParams = currentParams.filter((param) => param.name !== 'max_ad_duration');
        }

        // if the new Param cannot be found in the current params, add it
        if (
          currentParams.find((currentParam: IParam) => currentParam.name === newParam.name) ===
          undefined
        ) {
          currentParams.push(newParam);
        }
        // if param is ignored, always update its value since it will be dynamic
        if (ignoredParams.includes(newParam.name)) {
          const ignoredIndex = currentParams.findIndex(
            (currentParam: IParam) => currentParam.name === newParam.name,
          );
          if (ignoredIndex >= 0) {
            currentParams[ignoredIndex].value = newParam.value;
          }
        }
      });

      // delete currentParams that are not in newParams
      const toRemoveNames: string[] = [];
      currentParams.forEach((currentParam: IParam) => {
        if (
          newParams.find((newParam: IParam) => newParam.name === currentParam.name) === undefined
        ) {
          toRemoveNames.push(currentParam.name);
        }
      });

      const streamLiveParams = currentParams.filter(
        (currentParam: IParam) => !toRemoveNames.includes(currentParam.name),
      );
      const endpointURLParams = this.createParamsQueryURL(streamLiveParams);

      this.setState({
        streamLiveParams,
        EndpointURLParams: endpointURLParams,
      });
      // recreate URL with user-defined values instead of default values

      return this.generateURLWithCustomParams(endpoint, streamLiveParams);
    };

    // Return list of key-value params from given URL
    getParamsFromURL = (url: string): IParam[] => {
      const params: IParam[] = [];
      // split URL between domain and parameters - 'google.com/test?a=1&b=2' => ['google.com/test', 'a=1&b=2']
      const urlSplit = url.split('?');
      if (urlSplit.length < 2) {
        // no parameters, return empty params
        return params;
      }
      // split URL parameter part into parameters - 'a=1&b=2' => ['a=1', 'b=2']
      const paramSplit = urlSplit[1].split('&');
      for (const paramStr of paramSplit) {
        // split parameter in key + value - 'a=1' => key='a', value='1'
        const paramStrSplit = paramStr.split('=');
        if (paramStrSplit.length !== 2) {
          // incorrect parameter format => continue
          continue;
        }
        const key = paramStrSplit[0];
        const value = paramStrSplit.slice(1).join('='); // if '=' is present in the value, recreate the string
        // append to params
        params.push({ name: key, value });
      }
      return params;
    };

    /**
     * Creates the query part of the URL with the custom params
     * @param params List of params to create the query part of the URL
     * @returns URL query part. Example: 'network_id=497&live=0&avod=1'
     */
    createParamsQueryURL = (params: IParam[]): string => {
      const queryURL = params.map((param: IParam) => {
        if (param.name === 'network_id') {
          return `network_id=${this.state.ID}`;
        }

        return param.name.replace(' (invisible)', '') + '=' + param.value;
      });
      return [...new Set(queryURL)].join('&');
    };

    // Transforms URL with default-params to URL with user-custom params
    generateURLWithCustomParams = (endpoint: string, params: IParam[]): string => {
      // endpoint without parameters - 'test.com?lalala=test' => 'test.com'
      const domain = endpoint.split('?')[0];
      // parameter separation (if any)
      const separator = params.length > 0 ? '?' : '';
      const paramsStr = this.createParamsQueryURL(params);
      return domain + separator + paramsStr;
    };

    // Generates publica Ad URL for inventory split
    generatePublicaAdURL = (id: string, isRoute?: boolean): string => {
      // generate a PBS URL from the stitched-url
      const baseURL = NETWORK_AD_URL_SITE_PREFIX;
      // filter some params that shouldn't propagate between stiched-url and channel-split super-only URL
      const filteredParamNames: string[] = ['avod', 'live', 'network_id', 'vmap', 'u'];
      const filteredParams = this.state.streamLiveParams.filter(
        (param: IParam) => !filteredParamNames.includes(param.name),
      );
      // add stichedURL params to baseURL
      let adURL = this.generateURLWithCustomParams(baseURL, filteredParams);
      const idString = isRoute ? 'inventory_route_id' : 'site_id';
      // add specific params to adurl
      if (adURL.includes('?')) {
        adURL += `&format=vast&${idString}=` + id;
      } else {
        adURL += `?format=vast&${idString}=` + id;
      }
      return adURL;
    };

    /**
     * Pulls all house ads available to a publisher and adds them to state.
     * Updates dropdown and table rows with default of none selected
     * @param pubID
     */
    loadPublisherSSAIHouseAds = (pubID: string) => {
      getSSAIHouseAdsByPublisherID(pubID)
        .then((resp) => {
          const houseAds = this.state.HouseAds !== undefined ? this.state.HouseAds : [];
          const publisherSSAIHouseAds = resp.parsedBody !== undefined ? resp.parsedBody : [];
          this.initializeSSAIHouseAdsDropdownAndTableRows(publisherSSAIHouseAds, houseAds);
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading House Ads',
            ssaiHouseAdsLoadingState: LoadingStateNoData,
          });
        });
    };
    // -------- COMPONENT HELPER FUNCTIONS -------- //

    // Adds new blank tracker
    addTracker = (): void => {
      const newTracker = {
        ID: 0,
        NetworkID: this.state.ID,
        URL: '',
        Active: ResourceActiveEnabled,
        EventType: NetworkTrackerEventTypeStart,
      } as INetworkTracker;
      const NetworkTrackers = [...this.state.NetworkTrackers];
      NetworkTrackers.push(newTracker);
      this.setState({
        NetworkTrackers,
      });
    };

    // Adds new blank in video unit
    addInVideoUnit = (): void => {
      const newInVideoUnit = {
        ID: 0,
        NetworkID: this.state.ID,
        URL: '',
        Active: ResourceActiveEnabled,
        Partner: NetworkInVideoUnitPartnerTriplelift,
      } as INetworkInVideoUnit;
      const NetworkInVideoUnits = [...this.state.NetworkInVideoUnits];
      NetworkInVideoUnits.push(newInVideoUnit);
      this.setState({
        NetworkInVideoUnits,
      });
    };

    // NOTE: Make functions for permissions (super, canEdit, masking) to reduce potential errors
    isSuper = (): boolean => {
      return hasRole(this.props.user.user, [ROLE_SUPER]);
    };

    isUserInfoMasked = (): boolean => {
      return checkUserInfoMasked(this.props.user.user);
    };

    // Determines if the current publisher is an owner of this network
    canEdit = (): boolean => {
      if (this.isNewOrClonedNetwork()) {
        return true;
      }
      for (const networkMap of this.state.NetworkPublisherMaps) {
        if (
          networkMap.PublisherID.toString() === this.props.session.publisher.ID &&
          networkMap.NetworkPublisherRole === ENetworkRole.ROLE_OWNER
        ) {
          return true;
        }
      }
      return false;
    };

    // Determines if the current user can write and returns permissions
    hasWriteRights = (): IPermissionCheck => {
      return IS_AUTHORIZED(['write_network'], this.props.user.user);
    };

    // Validates channel information before saving
    validateChannel = (channel: INetworkChannel): string[] => {
      const channelErrors = [] as string[];
      if (channel.Name === '') {
        channelErrors.push('Name required');
      }
      if (channel.DemandSourceType === NetworkDemandSourceType.TYPE_UNKNOWN) {
        channelErrors.push('DemandSource required');
      }
      if (
        channel.DemandSourceType === NetworkDemandSourceType.TYPE_PUBLICA &&
        (channel.SiteID === undefined || channel.SiteID <= 0)
      ) {
        channelErrors.push('Select a channel');
      }
      if (
        channel.DemandSourceType === NetworkDemandSourceType.TYPE_VAST &&
        !checkURL.test(channel.AdURL)
      ) {
        channelErrors.push('Enter a valid VAST url');
      }
      if (
        channel.DemandSourceType === NetworkDemandSourceType.TYPE_ROUTE &&
        (channel.RouteID === undefined || channel.RouteID <= 0)
      ) {
        channelErrors.push('RouteID required');
      }

      if (channel.Weight === undefined || channel.Weight < 0) {
        channelErrors.push('Weight must be at least 0');
      }

      return channelErrors;
    };

    // Validates tracker information before saving
    validateTracker = (url: string): string[] => {
      const trackerErrors = [] as string[];
      if (!checkURL.test(url)) {
        trackerErrors.push('Tracker URL invalid');
      }
      if (url.length < 8 || url.slice(0, 8).toLocaleLowerCase() !== 'https://') {
        trackerErrors.push(`URL must start with 'https://'.`);
      }
      return trackerErrors;
    };

    // Validates network before it is sent to API to be created or updated
    // Returns errors for the user to fix before saving again
    validateNetwork = (): string[] => {
      const errors = [] as string[];
      // validate network object
      if (this.state.Active === ResourceActiveUnknown) {
        errors.push('Active unknown');
      }
      if (this.state.Name === '') {
        errors.push('Name empty');
      }
      if (this.state.Name === 'New Network') {
        errors.push('Please choose a custom name');
      }
      if (this.state.StreamType === NetworkStreamType.TYPE_UNKNOWN) {
        errors.push('StreamType unknown');
      }
      if (this.state.StreamType === NetworkStreamType.TYPE_LIVE && this.state.StreamURL === '') {
        errors.push('StreamURL empty');
      }
      // validate channels
      if (this.state.Channels.length === 0) {
        errors.push('Inventory Split Required');
      } else {
        for (const channel of this.state.Channels) {
          const channelErrors = this.validateChannel(channel);
          if (channelErrors.length) {
            errors.push(`Error in Inventory Split - ${channel.Name}`);
          }
        }
      }
      // validate trackers
      if (this.state.NetworkTrackers.length > 0) {
        for (const tracker of this.state.NetworkTrackers) {
          const trackerErrors = this.validateTracker(tracker.URL);
          if (trackerErrors.length) {
            errors.push(`Error in Tracker - ${tracker.ID}`);
          }
        }
      }
      // validate network publisher maps
      let ownerCount = 0;
      for (const map of this.state.NetworkPublisherMaps) {
        if (map.NetworkPublisherRole === ENetworkRole.ROLE_OWNER) {
          ownerCount++;
        }
      }
      // validate network in video units all have different partners and have valid urls
      const partners = [] as NetworkInVideoUnitPartner[];
      for (const unit of this.state.NetworkInVideoUnits) {
        if (partners.includes(unit.Partner)) {
          errors.push('Each partner can only have a single In Video Unit item');
          break;
        } else {
          partners.push(unit.Partner);
        }
        const inVideoUnitErrors = this.validateTracker(unit.URL);
        if (inVideoUnitErrors.length) {
          errors.push(`Error in In Video Unit - ${unit.ID}`);
        }
      }
      if (ownerCount === 0) {
        errors.push('Owner role required');
      } else if (ownerCount > 1) {
        errors.push('Only one owner role allowed');
      }

      // validate fetch on demand requirements
      if (
        this.state.StreamType === NetworkStreamType.TYPE_FOD &&
        this.state.ssaiHouseAdsTableRows.length === 0
      ) {
        errors.push('For Fetch on Demand, at least one house ad is required');
      }
      return errors;
    };

    // -------- HANDLER FUNCTIONS -------- //
    // NOTE: functions passed into a child component which update state in the parent
    // should be called handleFunc.  They are used to not directly modify the props/state
    // and make sure the parent component is properly re-rendered

    // -------- SETTINGS TOP BAR HANDLER FUNCTIONS -------- //

    handleNameChange = (name: string): void => {
      this.setState({
        Name: name,
      });
    };

    // Top bar close
    handleClose = (): void => {
      navigate(
        RouteNetworkList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    handleChangeActive = (): void => {
      this.setState({
        Active:
          this.state.Active === ResourceActiveEnabled
            ? ResourceActiveDisabled
            : ResourceActiveEnabled,
      });
    };

    handleSave = (navigatePage: boolean = true): void => {
      // save param part of URL as network endpoint-url params
      // google.com/test?a=b => ['google.com/test', 'a=b']
      const endpointParamSplit = this.state.stitchedURL.split('?');
      let EndpointURLParams = '';
      const validationErrors = this.validateNetwork();
      if (validationErrors.length > 0) {
        this.setState({
          snackbarError: true,
          snackbarMessage: validationErrors.join(', '),
          saveAttempted: true,
        });
        return;
      }
      // CHECK STREAM TYPE to get rid of site id and stream URL
      if (endpointParamSplit.length > 1) {
        // remove first part of URL split and re-join the rest
        EndpointURLParams = endpointParamSplit.slice(1).join('?');
      }
      this.setState(
        {
          EndpointURLParams,
          isUpdating: true,
          saveAttempted: true,
        },
        () => {
          // create network object
          const network = cloneDeep(this.state) as INetwork;
          network.HouseAds = this.state.publisherSSAIHouseAds.filter(
            (ad) =>
              ad.ID in this.state.ssaiHouseAdsDropdownRows &&
              this.state.ssaiHouseAdsDropdownRows[ad.ID.toString()].selected,
          );
          // if this is a clone or new, create network
          if (this.state.ID === 0) {
            createNetwork(network, this.props.session.publisher.ID)
              .then((val: IHttpResponse<INetwork>) => {
                if (navigatePage) {
                  const network = transformNetwork(val.parsedBody) as INetwork;
                  navigate(RouteNetworkSettingsExisting, extractRouteParams(this.props), {
                    net: network.ID.toString(),
                  });
                }
              })
              .catch(() => {
                this.setState({
                  isUpdating: false,
                  snackbarMessage: 'Failed to create network.',
                  snackbarError: true,
                });
              });
          } else {
            // updateNetwork is called with fullUpdate true since we are passing in the entire network
            // object including the children (NetworkTrackers, NetworkPublisherMaps and Channels)
            // all fields must be present since this will not selectively update fields
            updateNetwork(network, this.props.session.publisher.ID, true)
              .then((val: IHttpResponse<INetwork>) => {
                const network = transformNetwork(val.parsedBody) as INetwork;
                this.setState({
                  ...network,
                  isUpdating: false,
                  snackbarMessage: 'Successfully saved network.',
                  snackbarError: false,
                });
              })
              .catch(() => {
                this.setState({
                  isUpdating: false,
                  snackbarMessage: 'Failed to save network.',
                  snackbarError: true,
                });
              });
          }
        },
      );
    };

    // Top bar delete
    handleDelete = (): void => {
      this.setState(
        {
          isUpdating: true,
        },
        () => {
          // if this is a new network or cloned network, navigate to list
          if (this.state.ID === 0) {
            this.handleClose();
          } else {
            deleteNetwork(this.state.ID.toString(), this.props.session.publisher.ID)
              .then(() => {
                this.handleClose();
              })
              .catch((err: any) => {
                console.error(err);
                this.setState({
                  isUpdating: false,
                  snackbarMessage: 'Error while deleting network.',
                  snackbarError: true,
                });
              });
          }
        },
      );
    };

    handleURLEditorChange = (text: string): void => {
      if (this.canEdit() || this.isSuper()) {
        this.setState({
          StreamURL: text,
        });
      }
    };

    // -------- STREAM HANDLER FUNCTIONS -------- //
    handleUpdateSSAIHouseAdsDropdownAndTableRows = (newRows: IMenuList) => {
      this.applySSAIHouseAdsDropdownAndTableRows(newRows);
    };

    /**
     * Updates dropdown with new dropdown selection, then uses changes to generate updated table rows.
     * @param newRows New data to be dispayed in MaterialSearchDropdownMulti.
     */
    applySSAIHouseAdsDropdownAndTableRows = (newRows: IMenuList) => {
      const HouseAds = this.state.publisherSSAIHouseAds.filter(
        (ad) => newRows[ad.ID.toString()]?.selected,
      );
      const ssaiHouseAdsDropdownRows = newRows;
      const ssaiHouseAdsTableRows = this.buildSSAIHouseAdsTableRows(HouseAds);
      this.setState({
        HouseAds,
        ssaiHouseAdsDropdownRows,
        ssaiHouseAdsTableRows,
      });
    };

    /**
     * Builds table rows from all ads that were selected (which is checked against dropdown rows).
     * @returns The table rows.
     */
    buildSSAIHouseAdsTableRows = (houseAds: ISSAIHouseAd[]): IListViewTableRow[] => {
      return houseAds.map((ad) => this.buildSSAIHouseAdsTableRow(ad));
    };

    /**
     * Helper to build a single table from a given house ad
     * @param ad House ad
     * @returns new row
     */
    buildSSAIHouseAdsTableRow = (ad: ISSAIHouseAd): IListViewTableRow => {
      return {
        ID: {
          value: ad.ID.toString(),
          renderType: 'string' as ListViewTableDataCellRenderType,
        },
        Name: {
          value: ad.Name,
          renderType: 'string' as ListViewTableDataCellRenderType,
        },
        Hash: {
          value: ad.URL,
          jsx: (props: ICustomRenderCellJSXProps) => {
            const split = props.value.toString().split('/');
            return (
              <Cols
                style={{
                  alignItems: 'center',
                }}
              >
                <Col horizontalPadding>
                  <CopyButton copy={props.value.toString()} text='' />
                </Col>
                <Col>
                  <Text small>{`${split[5]}`}</Text>
                </Col>
              </Cols>
            );
          },
        },
        Actions: {
          value: '',
          cellStyle: {
            textAlign: 'center',
            paddingRight: spacing.extraLarge,
          },
          renderType: 'actions' as ListViewTableDataCellRenderType,
        },
        TranscodeStatus: {
          value: ad.TranscodeStatus as TranscodeStatusCodes,
          cellStyle: {
            textAlign: 'center',
            paddingRight: spacing.extraLarge,
          },
          jsx: (props: ICustomRenderCellJSXProps) => {
            switch (props.value) {
              case 'complete':
                return ChipTranscodeStatus(
                  publicaColors.jungleGreen,
                  publicaColors.mint,
                  'Complete',
                );
              case 'processing':
              case 'stale':
                return ChipTranscodeStatus(
                  publicaColors.yellow,
                  publicaColors.veryLightYellow,
                  'Processing',
                );
              case 'unknown':
              default:
                return ChipTranscodeStatus(
                  publicaColors.black,
                  publicaColors.veryLightGray,
                  'Pending',
                );
            }
          },
        },
        Status: {
          value: 'status',
          renderType: 'string' as ListViewTableDataCellRenderType,
        },
      };
    };

    /**
     * Builds the dropdowns for all house ads, and checks the dropdown + adds the table rows if the same ads, sets the state
     */
    initializeSSAIHouseAdsDropdownAndTableRows = (
      publisherSSAIHouseAds: ISSAIHouseAd[],
      houseAds: ISSAIHouseAd[],
    ) => {
      const selectedIDs = new Set<number>(
        houseAds.map((ad) => {
          return ad.ID;
        }),
      );
      const ssaiHouseAdsDropdownRows = {} as IMenuList;
      publisherSSAIHouseAds.forEach((ad) => {
        ssaiHouseAdsDropdownRows[ad.ID.toString()] = this.newSSAIHouseAdsDropdownRow(
          ad,
          selectedIDs.has(ad.ID),
        );
      });
      const ssaiHouseAdsTableRows = this.buildSSAIHouseAdsTableRows(houseAds);
      this.setState({
        publisherSSAIHouseAds,
        ssaiHouseAdsDropdownRows,
        ssaiHouseAdsTableRows,
        ssaiHouseAdsLoadingState: LoadingStateHasData,
      });
    };

    /**
     * Builds dropdown rows for uploaded house ads.
     * @param ads
     * @param selected is whether the new rows are selected
     * @returns The rows for the new ads, all selected.
     */
    buildSSAIHouseAdsDropdownRows = (ads: ISSAIHouseAd[], selected: boolean): IMenuList => {
      const rows = {} as IMenuList;
      ads.forEach((ad) => {
        rows[ad.ID.toString()] = this.newSSAIHouseAdsDropdownRow(ad, selected);
      });
      return rows;
    };

    /**
     * Helper to build one dropdown row for ad.
     * @param ad
     * @returns The single row.
     */
    newSSAIHouseAdsDropdownRow = (ad: ISSAIHouseAd, selected: boolean): IListItem => {
      return {
        ID: ad.ID.toString(),
        title: ad.Name,
        selected,
      };
    };

    // Switch stream types and refresh the endpoint builder
    handleSelectStreamTypeChange = (value: string): void => {
      this.setState(
        {
          StreamType: streamTypeStringToNetworkStream(value),
        },
        () => this.refreshNetworkEndpoint(),
      );
    };

    // Endpoint Custom Table / Stitched URL Handlers
    handleSaveEndpoint = (streamLiveParams: IParam[]): void => {
      // add new user-values to endpoint before saving
      const stitchedURL = this.generateURLWithCustomParams(
        this.state.stitchedURL,
        streamLiveParams,
      );
      this.setState({
        streamLiveParams,
        stitchedURL,
      });
    };

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        [target.name]: target.value,
      });
    };

    handleToggleNormalizeContentValues = () => {
      this.setState((prevState) => {
        return {
          NormalizeContentValues:
            prevState.NormalizeContentValues === EBool.EBool_TRUE
              ? EBool.EBool_FALSE
              : EBool.EBool_TRUE,
        };
      });
    };

    handleToggleAllowSilentAds = () => {
      this.setState((prevState) => {
        return {
          AllowSilentAds:
            prevState.AllowSilentAds === EBool.EBool_TRUE ? EBool.EBool_FALSE : EBool.EBool_TRUE,
        };
      });
    };

    // A generic form handler for form input elements.
    handleStreamFormInputChange = (elem: React.ChangeEvent<HTMLInputElement>): void => {
      const { type, name: path, value } = elem.target;
      let parsedValue;
      switch (type) {
        // for input[type=number] fields
        case 'number':
          parsedValue = parseInt(value, 10);
          break;
        // Everything else including <input> fields return strings
        default:
          parsedValue = value;
      }

      this.setState({
        [path]: parsedValue,
      });
    };

    // -------- INVENTORY SPLIT HANDLER FUNCTIONS -------- //

    // Deletes network channels from state
    handleDeleteNetworkChannel = (idx: number): void => {
      const deleteChannel = () => {
        const Channels = [...this.state.Channels];
        Channels.splice(idx, 1);
        this.setState({
          Channels,
          isUpdating: false,
          snackbarMessage: 'Successfully deleted inventory split.',
          snackbarError: false,
        });
      };

      this.setState(
        {
          isUpdating: true,
        },
        () => {
          const id = this.state.Channels[idx].ID;

          // If this is a new network or cloned network, delete it locally only
          if (id === 0) {
            deleteChannel();
            return;
          }

          deleteNetworkChannel(
            id.toString(),
            this.props.currentNetwork.ID.toString(),
            this.props.session.publisher.ID,
          )
            .then(() => {
              deleteChannel();
            })
            .catch(() => {
              this.setState({
                isUpdating: false,
                snackbarMessage: 'Failed to delete inventory split.',
                snackbarError: true,
              });
            });
        },
      );
    };

    // Handles changes to network channels
    handleChangeNetworkChannel = (idx: number, field: NetworkChannelField, value: any): void => {
      const Channels = [...this.state.Channels];
      const isPublicaType = Channels[idx].DemandSourceType === NetworkDemandSourceType.TYPE_PUBLICA;
      const isVastType = Channels[idx].DemandSourceType === NetworkDemandSourceType.TYPE_VAST;
      const isRouteType = Channels[idx].DemandSourceType === NetworkDemandSourceType.TYPE_ROUTE;
      if (
        field === 'DemandSourceType' &&
        (isPublicaType || isRouteType) &&
        value === NetworkDemandSourceType.TYPE_VAST
      ) {
        // if the field is demand source is switching to VAST from ROUTE or PUBLICA, delete old publica ad url and remove the SiteID
        Channels[idx].AdURL = '';
        Channels[idx].SiteID = undefined;
        Channels[idx].RouteID = undefined;
      } else if (
        field === 'DemandSourceType' &&
        isVastType &&
        [NetworkDemandSourceType.TYPE_PUBLICA, NetworkDemandSourceType.TYPE_ROUTE].includes(value)
      ) {
        // if the field is demand source is switching to PUBLICA or ROUTE from vast, generate publica ad url
        const isValueRoute = value === NetworkDemandSourceType.TYPE_ROUTE;
        let id = Channels[idx].SiteID || '';
        if (isValueRoute) {
          id = Channels[idx].RouteID || '';
        }
        Channels[idx].AdURL = this.generatePublicaAdURL(id.toString(), isValueRoute);
        Channels[idx].SiteID = undefined;
        Channels[idx].RouteID = undefined;
      } else if (field === 'SiteID' && isPublicaType) {
        // if the field is SiteID, generate the publicaAdURL to include new site ID
        Channels[idx].AdURL = this.generatePublicaAdURL(value);
        Channels[idx].RouteID = undefined;
      } else if (field === 'RouteID' && isRouteType) {
        // if the field is RouteID, generate the publicaAdURL to include new route ID
        Channels[idx].AdURL = this.generatePublicaAdURL(value, true);
        Channels[idx].SiteID = undefined;
      }
      // @ts-ignore
      Channels[idx][field] = value;
      this.setState({
        Channels,
      });
    };

    // Adds a new empty channel to state
    handleCreateNewChannel = (): void => {
      const Channels = [...this.state.Channels];
      Channels.push({
        ID: 0,
        NetworkID: this.state.ID,
        Name: 'inventory_split_' + makeid(),
        AdURL: '',
        AppBundleOverride: '',
        Weight: 1,
        DemandSourceType: NetworkDemandSourceType.TYPE_VAST,
        SiteID: 0,
      } as INetworkChannel);
      this.setState({
        Channels,
      });
    };

    // -------- NETWORK TRACKERS HANDLER FUNCTIONS -------- //
    // Handle change in the network tracker's active switch
    handleTrackerEventActiveSwitchChange = (index: number): void => {
      const NetworkTrackers = [...this.state.NetworkTrackers];
      if (index < NetworkTrackers.length) {
        NetworkTrackers[index].Active =
          NetworkTrackers[index].Active === ResourceActiveDisabled
            ? ResourceActiveEnabled
            : ResourceActiveDisabled;
      }
      this.setState({
        NetworkTrackers,
      });
    };

    // Handle change in the network tracker's pixel URL
    handleTrackerEventPixelURLChange = (text: string, index: number): void => {
      const NetworkTrackers = [...this.state.NetworkTrackers];
      if (index < NetworkTrackers.length) {
        NetworkTrackers[index].URL = text;
      }
      this.setState({
        NetworkTrackers,
      });
    };

    // Handle change in the network tracker's event type
    handleTrackerEventTypeChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: number,
    ): void => {
      const NetworkTrackers = [...this.state.NetworkTrackers];
      NetworkTrackers[index].EventType =
        parseInt(e.target.value, 10) === undefined ? 0 : parseInt(e.target.value, 10);
      this.setState({
        NetworkTrackers,
      });
    };

    // Returns promise for deleting event
    handleDeleteModelWithPromise = (trackerID: string) => {
      const tracker = this.state.NetworkTrackers.find(
        (tracker: INetworkTracker) => tracker.ID === parseInt(trackerID, 10),
      );
      // if the tracker is new, return promise which will call handleDeleteSuccess
      if (tracker && tracker.ID === 0) {
        return new Promise((resolve) => resolve(undefined));
      }
      return deleteNetworkTracker(
        trackerID,
        tracker ? tracker.NetworkID.toString() : '0',
        this.props.session.publisher.ID,
      );
    };

    // Handles error when the delete fails
    handleDeleteError = (): void => {
      this.setState({
        snackbarError: true,
        snackbarMessage: 'Tracker event deletion failed',
      });
    };

    // Handles message when the delete succeeds
    handleDeleteSuccess = (index: number): void => {
      const NetworkTrackers = [...this.state.NetworkTrackers];
      if (index !== undefined && index < NetworkTrackers.length) {
        NetworkTrackers.splice(index, 1);
        this.setState({
          NetworkTrackers,
          snackbarError: false,
          snackbarMessage: 'Event successfully deleted',
        });
        return;
      }
    };

    // -------- NETWORK IN VIDEO UNIT HANDLER FUNCTIONS -------- //
    // Handle change in the network in video unit's active switch
    handleInVideoUnitActiveSwitchChange = (index: number): void => {
      const NetworkInVideoUnits = [...this.state.NetworkInVideoUnits];
      if (index < NetworkInVideoUnits.length) {
        NetworkInVideoUnits[index].Active =
          NetworkInVideoUnits[index].Active === ResourceActiveDisabled
            ? ResourceActiveEnabled
            : ResourceActiveDisabled;
      }
      this.setState({
        NetworkInVideoUnits,
      });
    };

    // Handle change in the network in video unit's URL
    handleInVideoUnitURLChange = (text: string, index: number): void => {
      const NetworkInVideoUnits = [...this.state.NetworkInVideoUnits];
      if (index < NetworkInVideoUnits.length) {
        NetworkInVideoUnits[index].URL = text;
      }
      this.setState({
        NetworkInVideoUnits,
      });
    };

    // Handle change in the network in video unit's partner
    handleInVideoUnitPartnerChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: number,
    ): void => {
      const NetworkInVideoUnits = [...this.state.NetworkInVideoUnits];
      NetworkInVideoUnits[index].Partner =
        parseInt(e.target.value, 10) === undefined ? 0 : parseInt(e.target.value, 10);
      this.setState({
        NetworkInVideoUnits,
      });
    };

    // Returns promise for deleting event
    handleInVideoUnitDeleteModelWithPromise = (unitID: string) => {
      const inVideoUnit = this.state.NetworkInVideoUnits.find(
        (unit: INetworkInVideoUnit) => unit.ID === parseInt(unitID, 10),
      );
      // if the tracker is new, return promise which will call handleDeleteSuccess
      if (inVideoUnit && inVideoUnit.ID === 0) {
        return new Promise((resolve) => resolve(undefined));
      }
      return deleteNetworkInVideoUnit(
        unitID,
        inVideoUnit ? inVideoUnit.NetworkID.toString() : '0',
        this.props.session.publisher.ID,
      );
    };

    // Handles error when the delete fails
    handleInVideoUnitDeleteError = (): void => {
      this.setState({
        snackbarError: true,
        snackbarMessage: 'In Video Unit deletion failed',
      });
    };

    // Handles message when the delete succeeds
    handleInVideoUnitDeleteSuccess = (index: number): void => {
      const NetworkInVideoUnits = [...this.state.NetworkInVideoUnits];
      if (index !== undefined && index < NetworkInVideoUnits.length) {
        NetworkInVideoUnits.splice(index, 1);
        this.setState({
          NetworkInVideoUnits,
          snackbarError: false,
          snackbarMessage: 'In Video Unit successfully deleted',
        });
        return;
      }
    };

    // -------- ROLES HANDLER FUNCTIONS -------- //

    handleAddNetworkPublisherMap = (networkPublisherMap: INetworkPublisherRowData): void => {
      if (this.isSuper() || this.canEdit()) {
        this.setState({
          NetworkPublisherMaps: update(this.state.NetworkPublisherMaps, {
            $push: [networkPublisherMap],
          }),
        });
      }
    };

    handleUpdateNetworkPublisherMap = (
      networkPublisherMap: INetworkPublisherRowData,
      index: number,
    ): void => {
      if (this.isSuper() || this.canEdit()) {
        this.setState({
          NetworkPublisherMaps: update(this.state.NetworkPublisherMaps, {
            [index]: {
              $set: networkPublisherMap,
            },
          }),
        });
      }
    };

    handleDeleteNetworkPublisherMap = (index: number): void => {
      if (this.isSuper() || this.canEdit()) {
        this.setState(
          {
            isUpdating: true,
          },
          () => {
            const id = this.state.NetworkPublisherMaps[index].ID;
            deleteNetworkPublisherMap(
              id.toString(),
              this.props.currentNetwork.ID.toString(),
              this.props.session.publisher.ID,
            )
              .then(() => {
                this.setState({
                  NetworkPublisherMaps: update(this.state.NetworkPublisherMaps, {
                    $splice: [[index, 1]],
                  }),
                  isUpdating: false,
                  snackbarMessage: 'Successfully deleted role.',
                  snackbarError: false,
                });
              })
              .catch(() => {
                this.setState({
                  isUpdating: false,
                  snackbarMessage: 'Failed to delete role.',
                  snackbarError: true,
                });
              });
          },
        );
      }
    };

    // -------- SNACKBAR HELPER FUNCTIONS -------- //
    handleSnackbarError = (snackbarError: boolean, snackbarMessage: string): void => {
      this.setState({
        snackbarError,
        snackbarMessage,
      });
    };

    handleSnackbarClose = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    snackbarErrorUploadSSAIHouseAd = (msg?: string) => {
      this.setState({
        uploadingSSAIHouseAd: false,
        snackbarMessage: msg ? msg : 'Failed to upload mediafile',
        snackbarError: true,
      });
    };

    // -------- UPLOAD MEDIAFILE HELPER FUNCTIONS -------- //]

    isExtensionTypeSupported = (target: EventTarget & HTMLInputElement): boolean => {
      if (!(target.files && target.files.length > 0)) {
        return false;
      }
      const file = target.files[0];
      const filenameParams = file.name.split('.');
      const ext = filenameParams[filenameParams.length - 1];
      if (![EXTENSION_TYPE_MP4].includes(ext)) {
        this.snackbarErrorUploadSSAIHouseAd(
          `Currently the only supported mediafile format is ${EXTENSION_TYPE_MP4}. If required, please send a request to ${getContactEmail()}.`,
        );
        return false;
      }
      return true;
    };

    validateAndUploadSSAIHouseAdByURL = (url: string, customName: string) => {
      // Basic validity checks
      if (url.length < 4) {
        this.snackbarErrorUploadSSAIHouseAd('Invalid url');
        return;
      }
      if (url.slice(url.length - 4) !== '.mp4') {
        this.snackbarErrorUploadSSAIHouseAd('Currently only .mp4 files are supported');
        return;
      }
      if (customName.length === 0) {
        this.snackbarErrorUploadSSAIHouseAd('Files uploaded by URL must have custom names');
        return;
      }
      this.setState({
        uploadingSSAIHouseAd: true,
        snackbarMessage: 'Sent upload request',
        snackbarError: false,
      });
      uploadSSAIHouseAdByURL(this.props.session.publisher.ID, url, customName)
        .then((json: any) => {
          if (json.parsedBody === undefined) {
            if (json.error === undefined) {
              this.snackbarErrorUploadSSAIHouseAd('Upload failed due to unknown error');
              return;
            }
            this.snackbarErrorUploadSSAIHouseAd(json.Error.toString());
            return;
          }
          const newAds = json.parsedBody;
          if (newAds.length > 0) {
            try {
              this.setState(
                {
                  uploadingSSAIHouseAd: false,
                  snackbarMessage: 'Successfully uploaded mediafile',
                  snackbarError: false,
                  publisherSSAIHouseAds: [...this.state.publisherSSAIHouseAds, ...newAds],
                },
                () => {
                  // Adds new rows to old dropdown and updates table
                  this.handleUpdateSSAIHouseAdsDropdownAndTableRows({
                    ...this.state.ssaiHouseAdsDropdownRows,
                    ...this.buildSSAIHouseAdsDropdownRows([...newAds], true),
                  });
                },
              );
            } catch (e) {
              this.snackbarErrorUploadSSAIHouseAd();
            }
          } else {
            this.snackbarErrorUploadSSAIHouseAd();
          }
        })
        .catch(() => {
          this.snackbarErrorUploadSSAIHouseAd();
        });
    };

    uploadSSAIHouseAdMediafile = (
      { target }: React.ChangeEvent<HTMLInputElement>,
      customName: string,
    ) => {
      if (!(target.files && target.files.length > 0) || !this.isExtensionTypeSupported(target)) {
        return;
      }
      const file = target.files[0];
      const filenameParams = file.name.split('.');
      const uploadKey = this.uploadManager.addUpload(file.name);
      this.setState({ uploadingSSAIHouseAd: true });
      uploadSSAIHouseAd({
        publisher_id: this.props.session.publisher.ID,
        name: customName === '' ? filenameParams[0] : customName,
        file,
        managerFunc: (xhr: XMLHttpRequest) => {
          this.uploadManager.addListenersToUpload(uploadKey, xhr);
        },
      })
        .then((json: any) => {
          if (json.response === undefined) {
            if (json.error === undefined) {
              this.snackbarErrorUploadSSAIHouseAd('Upload failed due to unknown error');
              return;
            }
            this.snackbarErrorUploadSSAIHouseAd(json.Error.toString());
            return;
          }
          if (json.response.length > 0) {
            try {
              this.uploadManager.signalSuccess(uploadKey);
              this.setState({ uploadingSSAIHouseAd: false });
              this.setState(
                {
                  publisherSSAIHouseAds: [...this.state.publisherSSAIHouseAds, ...json.response],
                },
                () => {
                  this.handleUpdateSSAIHouseAdsDropdownAndTableRows({
                    ...this.state.ssaiHouseAdsDropdownRows,
                    ...this.buildSSAIHouseAdsDropdownRows([...json.response], true),
                  });
                },
              );
            } catch (e) {
              this.uploadManager.signalFailure(uploadKey);
              this.setState({ uploadingSSAIHouseAd: false });
            }
          } else {
            this.uploadManager.signalFailure(uploadKey);
            this.setState({ uploadingSSAIHouseAd: false });
          }
        })
        .catch(() => {
          this.uploadManager.signalFailure(uploadKey);
          this.setState({ uploadingSSAIHouseAd: false });
        });
    };

    uploadMediafile = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (!(target.files && target.files.length > 0) || !this.isExtensionTypeSupported(target)) {
        return;
      }
      const file = target.files[0];
      const filenameParams = file.name.split('.');
      const uploadKey = this.uploadManager.addUpload(file.name);
      const network = { ...this.state } as INetwork;
      this.setState({ uploadingSlateMedia: true });
      uploadNetworkSlatefile({
        publisher_id: this.props.session.publisher.ID,
        network_id: network.ID,
        name: filenameParams[0],
        file,
        managerFunc: (xhr: XMLHttpRequest) => {
          this.uploadManager.addListenersToUpload(uploadKey, xhr);
        },
      })
        .then((json: any) => {
          if (json.response.success) {
            try {
              this.setState({
                SlateURL: json.response.mediafile.url,
              });
              this.uploadManager.signalSuccess(uploadKey);
              this.setState({ uploadingSlateMedia: false });
            } catch (e) {
              this.uploadManager.signalFailure(uploadKey);
              this.setState({ uploadingSlateMedia: false });
            }
          } else {
            this.uploadManager.signalFailure(uploadKey);
            this.setState({ uploadingSlateMedia: false });
          }
        })
        .catch(() => {
          this.uploadManager.signalFailure(uploadKey);
          this.setState({ uploadingSlateMedia: false });
        });
    };

    uploadSSAIPadderAdMediafile = (
      changeEvent: React.ChangeEvent<HTMLInputElement>,
      padderType: EPadderType,
    ) => {
      // Generate the input configuration
      const input: IUploadSingleMediafileInput = {
        changeEvent,
        uploadManager: this.uploadManager,
        uploadConfig: {
          publisher_id: this.props.session.publisher.ID,
          network_id: this.props.currentNetwork.ID,
          padder_type: padderType,
          name: '',
          file: undefined,
        },
        setUploadingStatus: (isUploading: boolean) => {
          this.setState({ uploadingSSAIPadderAd: isUploading });
        },
        setSnackbarErrorMessage: this.handleSnackbarError,
        onSuccessCallback: this.unionSSAIPadderAds,
        uploadFunction: uploadSSAIPadderAd,
      };
      uploadSingleMediafile(input);
    };

    // -------- RENDER HELPER FUNCTIONS -------- //
    // NOTE: We destructure props and states used in render functions to clearly
    // show what we are passing as well as keeping the code clean and easier to read

    // Renders app bar => name edit, save, close, delete
    renderAppBar(): ReactElement {
      const { ID, Name, isUpdating } = this.state;
      return (
        <Grid item>
          <SettingsAppBar
            name={this.isUserInfoMasked() ? `Network_${ID}` : Name}
            isActive={this.state.Active === ResourceActiveEnabled}
            hasWriteRights={this.hasWriteRights().isAuthorized}
            isUpdating={isUpdating}
            handleSave={this.handleSave}
            handleChangeActive={this.handleChangeActive}
            relative
          />
        </Grid>
      );
    }

    // Render the dialog for live logs
    renderLiveLogsForNetwork() {
      const { classes } = this.props;
      const {
        ID,
        streamResponseLiveLogs,
        streamStartLiveLogs,
        refreshingLiveLogs,
        refreshingLiveLogsTimestamp,
      } = this.state;

      if (ID === 0) {
        return <></>;
      }

      return (
        <LiveLogsWrapper
          elements={[
            {
              title: 'Stream Start',
              render: (searchFilter: string) => {
                return (
                  <LiveLogsGeneralTable<IStreamStartData>
                    publisherTimezone={this.props.session.publisher.Timezone}
                    data={filterLiveLogs(streamStartLiveLogs, searchFilter)}
                    columns={[
                      {
                        title: 'Timestamp',
                        field: 'timestamp',
                        searchable: false,
                        width: '40px',
                        cellStyle: { whiteSpace: 'pre-wrap' },
                      },
                      {
                        title: 'Request URL',
                        field: 'requestURL',
                        searchable: false,
                      },
                    ]}
                    nestedData={[
                      {
                        title: 'User Agent',
                        getData: (row: IStreamStartData) => row.ua,
                        getDataToCopy: (row: IStreamStartData) => row.ua,
                        preFormat: 'text',
                      },
                      {
                        title: 'XFF',
                        getData: (row: IStreamStartData) => row.xff,
                        getDataToCopy: (row: IStreamStartData) => row.xff,
                        preFormat: 'text',
                      },
                    ]}
                  />
                );
              },
            },
            {
              title: 'Stream Response',
              render: (searchFilter: string) => {
                return (
                  <LogStreamResponseTable
                    data={filterLiveLogs(streamResponseLiveLogs, searchFilter)}
                  />
                );
              },
            },
          ]}
          classes={classes}
          getLiveLogs={() => this.getLiveLogs()}
          refreshingLiveLogs={refreshingLiveLogs}
          refreshingLiveLogsTimestamp={refreshingLiveLogsTimestamp}
        />
      );
    }

    // renders roles tab
    renderRolesTab = (): ReactElement => {
      const { classes } = this.props;
      const { NetworkPublisherMaps, publishers } = this.state;
      // generate publisher hash
      const publisherMap = {} as IPublisherMap;
      for (const publisher of publishers) {
        publisherMap[publisher.ID] = publisher;
      }
      return (
        <Grid className={classes.mainGridItem}>
          <PublisherRolesTable
            publisherMap={publisherMap}
            networkPublisherMaps={NetworkPublisherMaps}
            handleSnackbarError={this.handleSnackbarError}
            handleAddNetworkPublisherMap={this.handleAddNetworkPublisherMap}
            handleUpdateNetworkPublisherMap={this.handleUpdateNetworkPublisherMap}
            handleDeleteNetworkPublisherMap={this.handleDeleteNetworkPublisherMap}
          />
        </Grid>
      );
    };

    renderInventorySplitTab = (): ReactElement => {
      return (
        <div
          style={{
            marginTop: '20px',
            marginBottom: '150px',
            marginLeft: '10px',
          }}
        >
          <RenderSplitRoutes
            isSuper={this.isSuper}
            canEdit={this.canEdit}
            isNewOrClone={this.isNewOrClonedNetwork()}
            isUpdating={this.state.isUpdating}
            saveAttempted={this.state.saveAttempted}
            isUserInfoMasked={this.isUserInfoMasked}
            NetworkChannels={this.state.Channels}
            handleCreateNewNetworkChannel={this.handleCreateNewChannel}
            handleDeleteNetworkChannel={this.handleDeleteNetworkChannel}
            validateNetworkChannel={this.validateChannel}
            handleChangeNetworkChannel={this.handleChangeNetworkChannel}
          />
        </div>
      );
    };

    renderStreamURLTab = (): ReactElement => {
      return (
        <div
          style={{
            marginTop: '20px',
            marginBottom: '275px', // NOTE: temporary, because the dropdown will clip below the bottom of the page
            marginLeft: '10px',
          }}
        >
          {this.renderStreamURLForm(false)}
        </div>
      );
    };

    renderSettingsTab = (): ReactElement => {
      return (
        <Rows>
          <Row>
            <Text bold large>
              Basic Settings
            </Text>
          </Row>
          <Row>
            <Panel paper>
              <RenderBasicNetworkSettings
                Name={this.isUserInfoMasked() ? `Network_${this.state.ID}` : this.state.Name}
                handleNameChange={this.handleNameChange}
                StreamType={this.state.StreamType}
                isSuper={this.isSuper}
                canEdit={this.canEdit}
                handleSelectStreamTypeChange={this.handleSelectStreamTypeChange}
                StreamTimeout={this.state.StreamTimeout}
                InventoryTimeout={this.state.InventoryTimeout}
                handleStreamFormInputChange={this.handleStreamFormInputChange}
                StreamURL={this.state.StreamURL}
                handleURLEditorChange={this.handleURLEditorChange}
                RenderOnDemand={this.state.StreamType === NetworkStreamType.TYPE_FOD}
                handleTogglingRenderOnDemand={this.handleTogglingRenderOnDemand}
              />
            </Panel>
          </Row>
          <Row>
            <Text bold large>
              Optional Settings
            </Text>
          </Row>
          <Row>
            <Panel paper>
              <RenderOptionalNetworkSettings
                Username={this.props.user.user.Username}
                publisherID={this.props.session.publisher.ID}
                Timezone={this.props.session.publisher.Timezone}
                addTracker={this.addTracker}
                saveAttempted={this.state.saveAttempted}
                uploadingSlateMedia={this.state.uploadingSlateMedia}
                NetworkTrackers={this.state.NetworkTrackers}
                handleDeleteError={this.handleDeleteError}
                handleDeleteSuccess={this.handleDeleteSuccess}
                handleTrackerEventTypeChange={this.handleTrackerEventTypeChange}
                handleDeleteModelWithPromise={this.handleDeleteModelWithPromise}
                handleTrackerEventPixelURLChange={this.handleTrackerEventPixelURLChange}
                handleTrackerEventActiveSwitchChange={this.handleTrackerEventActiveSwitchChange}
                addInVideoUnit={this.addInVideoUnit}
                NetworkInVideoUnits={this.state.NetworkInVideoUnits}
                handleInVideoUnitURLChange={this.handleInVideoUnitURLChange}
                handleInVideoUnitDeleteError={this.handleInVideoUnitDeleteError}
                handleInVideoUnitPartnerChange={this.handleInVideoUnitPartnerChange}
                handleInVideoUnitDeleteSuccess={this.handleInVideoUnitDeleteSuccess}
                handleInVideoUnitActiveSwitchChange={this.handleInVideoUnitActiveSwitchChange}
                handleInVideoUnitDeleteModelWithPromise={
                  this.handleInVideoUnitDeleteModelWithPromise
                }
                SlateURL={this.state.SlateURL}
                updateSlateURL={(slateURL: string) => this.setState({ SlateURL: slateURL })}
                uploadMediafile={this.uploadMediafile}
                canWrite={this.hasWriteRights().isAuthorized}
                handleTargetingChange={this.handleChange}
                TargetingSCTECueIDs={this.state.TargetingSCTECueIDs}
                TargetingSCTECueIDsIsAllowed={this.state.TargetingSCTECueIDsIsAllowed}
                NormalizeContentValues={this.state.NormalizeContentValues}
                handleToggleNormalizeContentValues={this.handleToggleNormalizeContentValues}
                AllowSilentAds={this.state.AllowSilentAds}
                handleToggleAllowSilentAds={this.handleToggleAllowSilentAds}
              />
            </Panel>
          </Row>
        </Rows>
      );
    };

    setAudioNormalizationLevel = (level: number) => {
      this.setState({ AudioNormalizeTarget: level });
    };

    handleTogglingTranscoderV3 = () => {
      this.setState({
        EnableAudioNormalization: EBool.EBool_FALSE,
        EnableTranscoderV3:
          this.state.EnableTranscoderV3 === EBool.EBool_TRUE ? EBool.EBool_FALSE : EBool.EBool_TRUE,
      });
    };

    handleTogglingStartFromTop = () => {
      this.setState({
        EnableStartFromTop:
          this.state.EnableStartFromTop === EBool.EBool_TRUE ? EBool.EBool_FALSE : EBool.EBool_TRUE,
      });
    };

    handleTogglingMatchVariantLadder = () => {
      this.setState({
        EnableMatchVariantLadder:
          this.state.EnableMatchVariantLadder === EBool.EBool_TRUE
            ? EBool.EBool_FALSE
            : EBool.EBool_TRUE,
      });
    };

    handleTogglingPadders = () => {
      const nextStateSetting =
        this.state.EnableBumper === EBool.EBool_TRUE ? EBool.EBool_FALSE : EBool.EBool_TRUE;
      this.setState({
        EnableBumper: nextStateSetting,
        EnableJingle: nextStateSetting,
      });
    };

    handleTogglingAudioNormalization = () => {
      this.setState({
        EnableAudioNormalization:
          this.state.EnableAudioNormalization === EBool.EBool_TRUE
            ? EBool.EBool_FALSE
            : EBool.EBool_TRUE,
      });
    };

    handleTogglingRenderOnDemand = () => {
      this.setState({
        StreamType:
          this.state.StreamType === NetworkStreamType.TYPE_FOD
            ? NetworkStreamType.TYPE_VOD
            : NetworkStreamType.TYPE_FOD,
      });
    };

    setSSAIPadderAds = (newPadders: ISSAIPadderAd[]) => {
      this.setState({ PadderAds: newPadders });
    };

    unionSSAIPadderAds = (newPadders: ISSAIPadderAd[]) => {
      // set-union the padders, prefer keeping new padder
      const seen = {} as Record<EPadderType, ISSAIPadderAd>;
      this.state.PadderAds?.forEach((oldPadder) => (seen[oldPadder.PadderType] = oldPadder));
      newPadders?.forEach((newPadder) => (seen[newPadder.PadderType] = newPadder));
      this.setState({ PadderAds: Object.values(seen) });
    };

    renderBetaFeaturesTab = (): ReactElement => {
      return (
        <Panel paper marginTop={spacing.medium} marginLeft={spacing.medium}>
          <Div largePadding>
            <NetworkBetaFeatures
              betaChipRequired={this.isNewOrClonedNetwork()}
              EnableTranscoderV3={this.state.EnableTranscoderV3}
              toggleEnableTranscoderV3={this.handleTogglingTranscoderV3}
              EnableStartFromTop={this.state.EnableStartFromTop}
              toggleEnableStartFromTop={this.handleTogglingStartFromTop}
              EnableMatchVariantLadder={this.state.EnableMatchVariantLadder}
              toggleEnableMatchVariantLadder={this.handleTogglingMatchVariantLadder}
              EnableAudioNormalization={this.state.EnableAudioNormalization}
              toggleEnableAudioNormalization={this.handleTogglingAudioNormalization}
              AudioNormalizeTarget={this.state.AudioNormalizeTarget}
              setAudioNormalizationTarget={this.setAudioNormalizationLevel}
              EnablePadders={
                this.state.EnableBumper === EBool.EBool_TRUE &&
                this.state.EnableJingle === EBool.EBool_TRUE
              }
              toggleEnablePadders={this.handleTogglingPadders}
              uploadSSAIPadderAdMediafile={this.uploadSSAIPadderAdMediafile}
              PadderAds={this.state.PadderAds}
              setSSAIPadderAds={this.setSSAIPadderAds}
              publisherID={this.props.user.publisher.ID}
            />
          </Div>
        </Panel>
      );
    };

    renderHouseAdsTab = (): ReactElement => {
      return (
        <HouseAdsUploadForm
          isInWizard={false}
          uploadSSAIHouseAdMediafile={this.uploadSSAIHouseAdMediafile}
          uploadSSAIHouseAdByURL={this.validateAndUploadSSAIHouseAdByURL}
          ssaiHouseAdsDropdownRows={this.state.ssaiHouseAdsDropdownRows}
          handleUpdateSSAIHouseAdsDropdownAndTableRows={
            this.handleUpdateSSAIHouseAdsDropdownAndTableRows
          }
          ssaiHouseAdsTableRows={this.state.ssaiHouseAdsTableRows}
          loadingState={this.state.ssaiHouseAdsLoadingState}
        />
      );
    };

    // define tabs and corresponding render functions
    renderTabs(): ReactElement {
      const tabs: ITab[] = [
        {
          label: 'Inventory Route',
          render: this.renderInventorySplitTab,
        },
        {
          label: 'Stream URL',
          render: this.renderStreamURLTab,
        },
        {
          label: 'Settings',
          render: this.renderSettingsTab,
        },
        {
          label: 'Beta Features',
          render: this.renderBetaFeaturesTab,
        },
      ];
      if (this.state.StreamType === NetworkStreamType.TYPE_FOD) {
        tabs.push({
          label: 'House Ads',
          render: this.renderHouseAdsTab,
        });
      }
      if (this.isSuper()) {
        tabs.push({
          label: 'Roles *',
          render: this.renderRolesTab,
        });
      }

      return (
        <div style={{ marginTop: spacing.medium }}>
          <LiveData
            resourceIDs={this.state.ID.toString()}
            resourceType={'Stream'}
            timezone={this.props.session.publisher.Timezone}
            publisher={this.props.session.publisher}
            maxWidth='1200px'
          />
          <SettingsTabs tabs={tabs} marginTop={0} maxTabWidth='1200px' />
        </div>
      );
    }

    renderStreamURLForm = (includeWizardButtons: boolean): ReactElement => {
      return (
        <StreamURLForm
          canEdit={this.canEdit}
          isUpdating={this.state.isUpdating}
          StreamType={this.state.StreamType}
          stitchedURL={this.state.stitchedURL}
          includeWizardButtons={includeWizardButtons}
          streamLiveParams={this.state.streamLiveParams}
          handleSaveEndpoint={this.handleSaveEndpoint}
          onEndpointBuilderChange={this.onEndpointBuilderChange}
        />
      );
    };

    renderWizard = () => {
      const {
        ID,
        Name,
        StreamType,
        StreamTimeout,
        InventoryTimeout,
        StreamURL,
        isUpdating,
        saveAttempted,
        Channels,
        NetworkTrackers,
        NetworkInVideoUnits,
        SlateURL,
        uploadingSlateMedia,
      } = this.state;

      return (
        <Wizard
          stepNames={
            this.state.StreamType === NetworkStreamType.TYPE_FOD
              ? RENDER_ON_DEMAND_STEP_NAMES
              : WIZARD_STEP_NAMES
          }
          canSkipPages={!this.isNewOrClonedNetwork()}
          removeFalseChildren={true}
        >
          <BasicNetworkSettingsForm
            Name={this.isUserInfoMasked() ? `Network_${ID}` : Name}
            handleNameChange={this.handleNameChange}
            StreamType={StreamType}
            isSuper={this.isSuper}
            canEdit={this.canEdit}
            handleSelectStreamTypeChange={this.handleSelectStreamTypeChange}
            StreamTimeout={StreamTimeout}
            InventoryTimeout={InventoryTimeout}
            handleStreamFormInputChange={this.handleStreamFormInputChange}
            StreamURL={StreamURL}
            handleURLEditorChange={this.handleURLEditorChange}
            Session={this.props.session}
            User={this.props.user}
            RenderOnDemand={this.state.StreamType === NetworkStreamType.TYPE_FOD}
            handleTogglingRenderOnDemand={this.handleTogglingRenderOnDemand}
          />
          {this.state.StreamType === NetworkStreamType.TYPE_FOD && (
            <HouseAdsUploadForm
              isInWizard={true}
              uploadSSAIHouseAdMediafile={this.uploadSSAIHouseAdMediafile}
              uploadSSAIHouseAdByURL={this.validateAndUploadSSAIHouseAdByURL}
              ssaiHouseAdsDropdownRows={this.state.ssaiHouseAdsDropdownRows}
              handleUpdateSSAIHouseAdsDropdownAndTableRows={
                this.handleUpdateSSAIHouseAdsDropdownAndTableRows
              }
              ssaiHouseAdsTableRows={this.state.ssaiHouseAdsTableRows}
              loadingState={this.state.ssaiHouseAdsLoadingState}
            />
          )}
          <InventorySplitForm
            isSuper={this.isSuper}
            canEdit={this.canEdit}
            isUserInfoMasked={this.isUserInfoMasked}
            isUpdating={isUpdating}
            saveAttempted={saveAttempted}
            isNewOrClone={this.isNewOrClonedNetwork()}
            NetworkChannels={Channels}
            handleCreateNewNetworkChannel={this.handleCreateNewChannel}
            handleDeleteNetworkChannel={this.handleDeleteNetworkChannel}
            validateNetworkChannel={this.validateChannel}
            handleChangeNetworkChannel={this.handleChangeNetworkChannel}
          />
          {this.renderStreamURLForm(true)}
          <OptionalNetworkSettingsForm
            Username={this.props.user.user.Username}
            publisherID={this.props.session.publisher.ID}
            Timezone={this.props.session.publisher.Timezone}
            handleSave={this.handleSave}
            saveAttempted={saveAttempted}
            // Trackers
            NetworkTrackers={NetworkTrackers}
            handleTrackerEventActiveSwitchChange={this.handleTrackerEventActiveSwitchChange}
            handleTrackerEventTypeChange={this.handleTrackerEventTypeChange}
            handleTrackerEventPixelURLChange={this.handleTrackerEventPixelURLChange}
            handleDeleteModelWithPromise={this.handleDeleteModelWithPromise}
            handleDeleteSuccess={this.handleDeleteSuccess}
            handleDeleteError={this.handleDeleteError}
            addTracker={this.addTracker}
            // In Video Units
            NetworkInVideoUnits={NetworkInVideoUnits}
            addInVideoUnit={this.addInVideoUnit}
            handleInVideoUnitURLChange={this.handleInVideoUnitURLChange}
            handleInVideoUnitDeleteError={this.handleInVideoUnitDeleteError}
            handleInVideoUnitPartnerChange={this.handleInVideoUnitPartnerChange}
            handleInVideoUnitDeleteSuccess={this.handleInVideoUnitDeleteSuccess}
            handleInVideoUnitActiveSwitchChange={this.handleInVideoUnitActiveSwitchChange}
            handleInVideoUnitDeleteModelWithPromise={this.handleInVideoUnitDeleteModelWithPromise}
            // Slate
            SlateURL={SlateURL}
            uploadingSlateMedia={uploadingSlateMedia}
            updateSlateURL={(slateURL: string) => this.setState({ SlateURL: slateURL })}
            uploadMediafile={this.uploadMediafile}
            canWrite={this.hasWriteRights().isAuthorized}
            handleTargetingChange={this.handleChange}
            TargetingSCTECueIDs={this.state.TargetingSCTECueIDs}
            TargetingSCTECueIDsIsAllowed={this.state.TargetingSCTECueIDsIsAllowed}
            NormalizeContentValues={this.state.NormalizeContentValues}
            handleToggleNormalizeContentValues={this.handleToggleNormalizeContentValues}
            AllowSilentAds={this.state.AllowSilentAds}
            handleToggleAllowSilentAds={this.handleToggleAllowSilentAds}
            // Beta Features
            BetaFeaturesAndHandlers={{
              betaChipRequired: this.isNewOrClonedNetwork(),
              EnableTranscoderV3: this.state.EnableTranscoderV3,
              toggleEnableTranscoderV3: this.handleTogglingTranscoderV3,
              EnableStartFromTop: this.state.EnableStartFromTop,
              toggleEnableStartFromTop: this.handleTogglingStartFromTop,
              EnableMatchVariantLadder: this.state.EnableMatchVariantLadder,
              toggleEnableMatchVariantLadder: this.handleTogglingMatchVariantLadder,
              EnableAudioNormalization: this.state.EnableAudioNormalization,
              toggleEnableAudioNormalization: this.handleTogglingAudioNormalization,
              AudioNormalizeTarget: this.state.AudioNormalizeTarget,
              setAudioNormalizationTarget: this.setAudioNormalizationLevel,
              EnablePadders:
                this.state.EnableBumper === EBool.EBool_TRUE &&
                this.state.EnableJingle === EBool.EBool_TRUE,
              toggleEnablePadders: this.handleTogglingPadders,
              uploadSSAIPadderAdMediafile: this.uploadSSAIPadderAdMediafile,
              PadderAds: this.state.PadderAds,
              setSSAIPadderAds: this.setSSAIPadderAds,
              publisherID: this.props.user.publisher.ID,
            }}
          />
        </Wizard>
      );
    };

    render() {
      const { loaded, snackbarMessage, snackbarError } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;

      if (loaded) {
        return (
          <Panels maxWidth vertical relative minWidthMain>
            <Panel
              largeHorizontalPadding
              verticalPadding
              style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
            >
              <ModelTopBar
                title={routeName(pathname)}
                session={session}
                user={user}
                publisher={session.publisher}
                classes={classes}
                delegateElevate={true}
                currentPath={pathname}
                disableTitlePaddingLeft
              />
            </Panel>
            {/* {this.state.ID !== 0 &&
              <Grid style={{ marginTop: 60 }}>
                <LiveData
                  resourceIDs={ID.toString()}
                  resourceType={'Stream'}
                  timezone={this.props.session.publisher.Timezone}
                  publisher={this.props.session.publisher}
                  maxWidth='1200px'
                />
              </Grid>
            } */}
            {!this.isNewOrClonedNetwork() && <Panel>{this.renderAppBar()}</Panel>}
            <Panel scroll largePadding>
              {this.isNewOrClonedNetwork() ? this.renderWizard() : this.renderTabs()}
              <Snackbar
                open={true}
                anchorOrigin={{
                  horizontal: 'right',
                  vertical: 'bottom',
                }}
              >
                <ModelSnackBarProgressBarContainer uploadManager={this.uploadManager} />
              </Snackbar>
              <SnackbarView
                open={!!snackbarMessage}
                error={!!snackbarError}
                onClose={this.handleSnackbarClose}
              >
                {snackbarMessage}
              </SnackbarView>
            </Panel>
            {this.renderLiveLogsForNetwork()}
          </Panels>
        );
      } else {
        return <LoadingView active={true} text='Loading the network settings' />;
      }
    }
  },
);
